<template>
  <Card>
    <template #title="">
      <div class="p-d-flex p-justify-between">
        Competency
        <div>
          <Button
            label="สร้างใหม่"
            icon="pi pi-plus"
            class="p-ml-2"
            @click="onNewCompetency"
          ></Button>
          <back-button class="p-ml-2" />
        </div>
      </div>
    </template>
    <template #content="">
      <div class="p-grid">
        <div class="p-col-12">
          <div class="p-grid">
            <div class="p-col-12 p-lg-4 p-md-6 p-fluid">
              <div class="p-inputgroup">
                <span class="p-inputgroup-addon">
                  <i class="pi pi-search"></i>
                </span>
                <InputText
                  v-model="searchString"
                  placeholder="ระบุคำค้น"
                ></InputText>
              </div>
            </div>
          </div>
        </div>
        <div class="p-col-12">
          <DataTable
            :paginator="true"
            :rows="10"
            :rowsPerPageOptions="[10, 20, 50]"
            :value="filteredCompetency"
            class="p-datatable-sm"
            ref="competencyTable"
            dataKey="id"
          >
            <template #empty>ไม่พบข้อมูล</template>
            <Column field="id" header="รหัส" :sortable="true"> </Column>
            <Column
              field="descriptionEN"
              header="Description (EN)"
              :sortable="true"
            >
            </Column>
            <Column
              field="descriptionTH"
              header="Description (TH)"
              :sortable="true"
            >
            </Column>
            <Column headerStyle="width: 10rem;" bodyStyle="width: 10rem;">
              <template #body="props">
                <Button
                  class="p-button-rounded p-button-success p-mr-2"
                  icon="pi pi-pencil"
                  @click="onEditCompetency(props.data)"
                ></Button>
                <Button
                  class="p-button-rounded p-button-warning"
                  icon="pi pi-trash"
                  @click="onDeleteCompetency(props.data)"
                ></Button>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>
  <Dialog v-model:visible="showDialog" :modal="true" class="p-fluid">
    <div class="p-field">
      <label for="competencyId">รหัส</label>
      <InputText
        maxlength="3"
        id="username"
        v-model.trim="competency.id"
        required="true"
        :disabled="existCompetencyId"
        autofocus
        :class="{ 'p-invalid': submitted && !competency.id }"
      >
      </InputText>
      <small class="p-error" v-if="submitted && !competency.id">
        โปรดกรอกรหัส
      </small>
      <small class="p-error" v-if="duplicateCompetencyId"> รหัสซ้ำ </small>
    </div>
    <div class="p-field">
      <label for="name">Description (EN)</label>
      <InputText
        id="name"
        v-model="competency.descriptionEN"
        maxlength="50"
      ></InputText>
    </div>
    <div class="p-field">
      <label for="name">Description (TH)</label>
      <InputText
        id="name"
        v-model="competency.descriptionTH"
        maxlength="50"
      ></InputText>
    </div>
    <template #footer>
      <Button
        icon="pi pi-check"
        label="ยืนยัน"
        class="p-button-success"
        @click="onSaveCompetency"
      ></Button>
      <Button
        icon="pi pi-times"
        label="ยกเลิก"
        class="p-button-danger p-ml-2"
        @click="onHideDialog"
      ></Button>
    </template>
  </Dialog>
  <!-- <PopupConfirmDelete
		:value="competency.competencyId"
		v-if="popupDeleteDialog"
	/> -->
  <Dialog
    v-model:visible="popupDeleteDialog"
    :style="{ width: '450px' }"
    header="Confirm"
    :modal="true"
  >
    <div class="confirmation-content">
      <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem" />
      <span v-if="competency"
        >Are you sure you want to delete <b>{{ competency.id }}</b
        >?</span
      >
    </div>
    <template #footer>
      <Button
        label="No"
        icon="pi pi-times"
        class="p-button-text"
        @click="popupDeleteDialog = false"
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        class="p-button-text"
        @click="handleDeleteCompetency"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
// import PopupConfirmDelete from "@/components/PopupConfirmDelete.vue";
import { ref, onMounted, computed, defineComponent } from "vue";
import { usePlugin } from "@/plugins";
import { clone, equals } from "ramda";
import {
  Competency,
  GetCompetencyResponse,
  UpsertCompetencyRequest,
} from "@/types";
import BackButton from "@/components/BackButton.vue";
import Fuse from "fuse.js";

export default {
  name: "Competency",
  components: { BackButton },
  setup() {
    const { success, error, axios, setLoading, padZero } = usePlugin();

    const competencyTable = ref(null);
    const competencies = ref<Competency[]>([]);
    const competency = ref<Competency>({} as Competency);
    const showDialog = ref(false);
    const popupDeleteDialog = ref(false);
    const searchString = ref("");
    const submitted = ref(false);
    const existCompetencyId = ref(false);
    const duplicateCompetencyId = ref(false);

    const filteredCompetency = computed(() => {
      if (!searchString.value) return competencies.value;
      const fuse = new Fuse(competencies.value, {
        keys: ["competencyId", "descriptionEN", "descriptionTH"],
      });
      const searchResult = fuse.search(searchString.value);
      return searchResult.map((x) => x.item);
    });

    const loadCompetencies = async () => {
      setLoading(true);
      try {
        const result = await axios.get<GetCompetencyResponse>(
          "api/v1/services/competency/get"
        );
        competencies.value = result.data.competencies ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onNewCompetency = () => {
      competency.value = {} as Competency;
      showDialog.value = true;
      submitted.value = false;
      existCompetencyId.value = false;
    };

    const onSaveCompetency = async () => {
      submitted.value = true;
      if (competency.value.id && competency.value.id.length > 0) {
        if (
          !existCompetencyId.value &&
          competencies.value.find(
            (comp) => comp.id === padZero(competency.value.id, 3)
          )
        ) {
          //Create competency ID
          duplicateCompetencyId.value = true;
          return;
        }
        setLoading(true);
        try {
          const result = await axios.post("api/v1/services/competency/upsert", {
            competencies: [
              {
                id: padZero(competency.value.id, 3),
                descriptionEN: competency.value.descriptionEN,
                descriptionTH: competency.value.descriptionTH,
              },
            ],
          } as UpsertCompetencyRequest);
          await loadCompetencies();
          success("บันทึกข้อมูลสำเร็จแล้ว");
          showDialog.value = false;
        } catch (e) {
          error(e);
        } finally {
          setLoading(false);
        }
      }
    };

    const onHideDialog = () => {
      showDialog.value = false;
      submitted.value = false;
      duplicateCompetencyId.value = false;
    };

    const onEditCompetency = (source: Competency) => {
      competency.value = clone(source);
      showDialog.value = true;
      existCompetencyId.value = true;
    };

    const onDeleteCompetency = (source: Competency) => {
      competency.value = clone(source);
      popupDeleteDialog.value = true;
    };

    const handleDeleteCompetency = async () => {
      setLoading(true);
      try {
        const result = await axios.delete("api/v1/services/competency/delete", {
          data: {
            competencies: [competency.value],
          } as UpsertCompetencyRequest,
        });
        await loadCompetencies();
        success("ลบข้อมูลสำเร็จแล้ว");
        popupDeleteDialog.value = false;
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
      // }
    };

    onMounted(async () => {
      await loadCompetencies();
    });
    return {
      competencyTable,
      competency,
      competencies,
      filteredCompetency,
      showDialog,
      submitted,
      existCompetencyId,
      duplicateCompetencyId,
      popupDeleteDialog,
      searchString,
      onNewCompetency,
      onEditCompetency,
      onDeleteCompetency,
      onSaveCompetency,
      handleDeleteCompetency,
      onHideDialog,
    };
  },
};
</script>

<style></style>
